import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AdCopyReviewerModal from '@modals/AdCopyReviewerModal'
import AdCopyReviewRequestsModal from '@modals/AdCopyReviewRequestsModal'
import ManageProviderOrderItemStatusModal from '@modals/ManageProviderOrderItemStatusModal'
import ShareProjectModal from '@modals/ShareProjectModal'

import type {
  AdCopyModel, CampaignModel, OrderItemModel, ProviderModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showAdCopyReviewerModal: false,
  showAdCopyReviewRequestsModal: false,
  showManageProviderOrderItemStatusModal: false,
  showShareProjectModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AdCopyReviewerModal: {
      closeModal: () => setState({ showAdCopyReviewerModal: false }),
    },
    AdCopyReviewRequestsModal: {
      closeModal: () => setState({ showAdCopyReviewRequestsModal: false }),
    },
    ManageProviderOrderItemStatusModal: {
      closeModal: () => setState({ showManageProviderOrderItemStatusModal: false }),
      updateProviderOrderItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ShareProjectModal: {
      closeModal: () => setState({ showShareProjectModal: false }),
      shareProject: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Copywriting = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showAdCopyReviewerModal,
    showAdCopyReviewRequestsModal,
    showManageProviderOrderItemStatusModal,
    showShareProjectModal,
  } = state

  const { campaignId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showAdCopyReviewerModal: (payload: {
        campaign: CampaignModel,
        orderItem: OrderItemModel,
        provider: ProviderModel,
      }) => {
        setModalData('AdCopyReviewerModal', payload)
        setState({ showAdCopyReviewerModal: true })
      },
      showAdCopyReviewRequestsModal: (payload: { adCopy: AdCopyModel, campaignId: number }) => {
        setModalData('AdCopyReviewRequestsModal', payload)
        setState({ showAdCopyReviewRequestsModal: true })
      },
      showManageProviderOrderItemStatusModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('ManageProviderOrderItemStatusModal', payload)
        setState({ showManageProviderOrderItemStatusModal: true })
      },
      showShareProjectModal: (payload: {
        campaign: CampaignModel,
        orderItem: OrderItemModel,
        provider: ProviderModel,
      }) => {
        setModalData('ShareProjectModal', payload)
        setState({ showShareProjectModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaignId={campaignId} />

        <AdCopyReviewerModal
          callbacks={callbacks('AdCopyReviewerModal', setState)}
          showModal={showAdCopyReviewerModal}
        />

        <AdCopyReviewRequestsModal
          callbacks={callbacks('AdCopyReviewRequestsModal', setState)}
          showModal={showAdCopyReviewRequestsModal}
        />

        <ManageProviderOrderItemStatusModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageProviderOrderItemStatusModal', setState)}
          showModal={showManageProviderOrderItemStatusModal}
        />

        <ShareProjectModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ShareProjectModal', setState)}
          isAdCopy
          showModal={showShareProjectModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Copywriting
